import React, { useEffect, useState } from 'react';
import { translate } from '../../../Services/translation';
import {
  ExtapiInvoice,
  ExtapiInvoiceSearchQueryParams,
  ExtapiInvoiceSearchResult,
} from '../../../Types/MyPages/InvoicesModel';
import { defaultInvoiceQueryParams } from '../rma/MyPagesRmaDefaults';
import {
  getInvoiceHeaders,
  getInvoicePdf,
} from '../../../Actions/Invoices.action';
import { useSearchParams } from 'react-router-dom';
import MyPagesInvoiceFilterForm from './MyPagesInvoiceFilterForm';
import MyPagesInvoiceList from './MyPagesInvoiceList';

export default function MyPagesInvoices() {
  const [invoices, setInvoices] = useState<ExtapiInvoiceSearchResult>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [queryParams, setQueryParams] = useState<
    ExtapiInvoiceSearchQueryParams | undefined
  >();

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.size == 0 || !!queryParams) {
      fetchInvoices(queryParams);
      return;
    }

    const queryParamsFromUrl = {
      startDate:
        searchParams.get('startDate') ??
        defaultInvoiceQueryParams.startDate,
      endDate:
        searchParams.get('endDate') ?? defaultInvoiceQueryParams.endDate,
      filterField:
        searchParams.get('filterField') ??
        defaultInvoiceQueryParams.filterField,
      filterFieldSearchValue:
        searchParams.get('filterFieldSearchValue') ??
        defaultInvoiceQueryParams.filterFieldSearchValue,
    };

    setQueryParams(queryParamsFromUrl);

    fetchInvoices(queryParamsFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInvoices = async (
    queryParams?: ExtapiInvoiceSearchQueryParams | undefined
  ) => {
    setIsLoading(true);
    if (!!queryParams) {
      let urlSearchParams = new URLSearchParams();
      for (let key of Object.keys(queryParams)) {
        let value = queryParams[key];
        if (!value) continue;

        urlSearchParams.append(key, value);
      }
      setSearchParams(urlSearchParams);
    }

    try {
      const data = await getInvoiceHeaders(queryParams);
      setErrorMessage(undefined);
      const result = {
        hits: data.length,
        result: data,
      } as ExtapiInvoiceSearchResult;
      setInvoices(result);
    } catch (error) {
      if (error.response.status == 404) {
        setErrorMessage(translate('mypage.invoices.filter-no-result'));
      } else {
        setErrorMessage(translate('general.error'));
      }
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadInvoice = async (invoice: ExtapiInvoice) => {
    if (!invoice.canDownload) return;

    var data = await getInvoicePdf(invoice.documentNo);
    const url = URL.createObjectURL(data);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${invoice.documentNo}.pdf`;
    anchor.click();
  };

  return (
    <div className="col-span-3 flex flex-col gap-4">
      <h1>{translate('mypage.invoices.title')}</h1>
      <p className="max-w-3xl text-lg">
        {translate('mypage.invoices.description')}
      </p>
      <MyPagesInvoiceFilterForm
        filter={queryParams}
        onFilterChange={setQueryParams}
        onSearch={() => fetchInvoices(queryParams)}
        isLoading={isLoading}
      />
      {errorMessage && (
        <small className="text-error-500">{errorMessage}</small>
      )}
      <MyPagesInvoiceList
        isLoading={isLoading}
        hasError={!!errorMessage}
        invoices={invoices?.result}
        onDownloadInvoice={downloadInvoice}
      />
    </div>
  );
}
